import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy } from "lucide-react";

const CRITERIA = ["Problem/Market", "Solution", "Business Model", "Team", "Traction", "Ask & Use of Funds"];

export default function Judge() {
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");

  useEffect(() => { (async () => {
    const { data } = await api.get("/judge/submissions");
    setSubmissions(data.submissions || []);
    if (data.submissions?.length) setSelected(data.submissions[0]);
  })(); }, []);

  useEffect(() => {
    const seed = {};
    CRITERIA.forEach((c) => (seed[c] = 5));
    setScores(seed);
    setNotes("");
  }, [selected?.id]);

  const submit = async () => {
    if (!selected) return;
    try {
      await api.post(`/judge/score/${selected.id}`, { scores, notes });
      toast.success("Score submitted");
    } catch (e) { toast.error("Score submission failed"); }
  };

  const total = Object.values(scores).reduce((a, b) => a + Number(b), 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">Judge Dashboard</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Pitch Competition</h1>
        <p className="mt-2 text-sm text-stone-600">Review submissions, score, and add notes.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border border-stone-200 bg-white">
          <div className="border-b border-stone-200 p-3 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-slate-950" />
            <div className="label-mono">Submissions ({submissions.length})</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-stone-100">
            {submissions.length === 0 && <div className="p-6 text-xs text-stone-500">No pitch submissions yet.</div>}
            {submissions.map((s) => (
              <button key={s.id} onClick={() => setSelected(s)} className={`block w-full p-4 text-left transition-colors ${selected?.id === s.id ? "bg-stone-100" : "hover:bg-stone-50"}`}>
                <div className="text-sm font-medium text-slate-950">{s.title}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-stone-500">{new Date(s.created_at).toLocaleDateString()}</div>
              </button>
            ))}
          </div>
        </div>
        {selected ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="card-flat max-h-[75vh] overflow-y-auto">
              <div className="label-mono mb-2">Pitch Outline</div>
              <h2 className="font-display text-lg font-medium text-slate-950">{selected.title}</h2>
              <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">{selected.content}</div>
            </div>
            <div className="card-flat">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-mono">Score Card</div>
                  <div className="mt-2 font-display text-2xl font-medium text-slate-950">Total: <span className="text-emerald-600">{total}</span>/60</div>
                </div>
              </div>
              <div className="mt-6 space-y-4">
                {CRITERIA.map((c) => (
                  <div key={c}>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-950">{c}</label>
                      <div className="font-mono text-sm text-slate-950">{scores[c] ?? 0}/10</div>
                    </div>
                    <input type="range" min={0} max={10} step={1} value={scores[c] ?? 0} onChange={(e) => setScores({ ...scores, [c]: Number(e.target.value) })} className="mt-2 w-full accent-slate-950" />
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <div className="label-mono mb-1">Notes</div>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
              </div>
              <button onClick={submit} className="btn-primary mt-4 w-full">Submit Score</button>
            </div>
          </div>
        ) : (
          <div className="card-flat flex h-64 items-center justify-center text-sm text-stone-500">Select a submission.</div>
        )}
      </div>
    </div>
  );
}
